import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { currentAccountId, runApi } from "../lib/runApi";
import type { DashboardPage as DashboardPageData, DashboardRow } from "../lib/runTypes";

const STATUS_FILTERS = ["", "idle", "scheduled", "running", "queued", "disabled"] as const;

function statusClass(status: string): string {
  switch (status) {
    case "running":
      return "tj-badge tj-badge-amber";
    case "error":
      return "tj-badge tj-badge-red";
    case "complete":
      return "tj-badge tj-badge-green";
    default:
      return "tj-badge";
  }
}

function verificationBadge(row: DashboardRow): string {
  const run = row.latest_run;
  if (!run) return "—";
  const failed = run.verification.failed + run.verification.mandatory_failed;
  if (failed > 0) return `${failed} fail`;
  return `${run.verification.passed} pass · ${run.verification.not_applicable} n/a`;
}

function duration(ms?: number | null): string {
  if (!ms) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function DashboardPage() {
  const accountId = currentAccountId();
  const [status, setStatus] = useState<string>("");
  const [data, setData] = useState<DashboardPageData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [emailNotice, setEmailNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await runApi.dashboard(accountId, status || undefined));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setLoading(false);
    }
  }, [accountId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void runApi
      .getNotificationEmail(accountId)
      .then((result) => setNotificationEmail(result.email ?? ""))
      .catch(() => undefined);
  }, [accountId]);

  // Usage is fetched on focus and every 60s, not written to Firestore.
  useEffect(() => {
    const timer = window.setInterval(() => void load(), 60_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  return (
    <main className="tj-container space-y-6 py-8" data-testid="dashboard-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="serif-heading text-2xl">Dashboard</h1>
          <p className="text-sm text-[var(--tj-muted)]">
            Counts and status come from run records, never from the schema.
          </p>
        </div>
        <div className="tj-nav-links">
          <select
            aria-label="Filter by status"
            className="tj-select"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {STATUS_FILTERS.map((value) => (
              <option key={value || "all"} value={value}>
                {value ? value.replace("_", " ") : "All statuses"}
              </option>
            ))}
          </select>
          <button className="tj-btn tj-btn-secondary" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </header>

      {error ? <p className="tj-badge tj-badge-red">{error}</p> : null}
      {loading && !data ? <p className="text-sm text-[var(--tj-muted)]">Loading dashboard…</p> : null}

      {data ? (
        <>
          <section className="tj-grid-3" aria-label="Task status counts">
            {Object.entries(data.counts).map(([key, count]) => (
              <div key={key} className="tj-card">
                <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">{key}</p>
                <p className="serif-heading text-2xl">{count}</p>
              </div>
            ))}
            {Object.keys(data.counts).length === 0 ? (
              <p className="text-sm text-[var(--tj-muted)]">No tasks yet.</p>
            ) : null}
          </section>

          <section className="tj-card space-y-2" aria-label="Notification email">
            <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">
              Failure notifications
            </p>
            <div className="flex flex-wrap gap-2">
              <input
                className="tj-input flex-1"
                type="email"
                placeholder="ops@example.com"
                value={notificationEmail}
                onChange={(event) => setNotificationEmail(event.target.value)}
              />
              <button
                className="tj-btn tj-btn-secondary"
                disabled={!notificationEmail.includes("@")}
                onClick={() =>
                  void runApi
                    .setNotificationEmail(accountId, notificationEmail.trim())
                    .then(() => setEmailNotice("Notification email saved."))
                    .catch((cause) =>
                      setEmailNotice(cause instanceof Error ? cause.message : String(cause)),
                    )
                }
              >
                Save
              </button>
            </div>
            {emailNotice ? (
              <p className="text-xs text-[var(--tj-muted)]">{emailNotice}</p>
            ) : null}
          </section>

          <section className="tj-grid-3" aria-label="DeepSeek usage">
            {data.usage.map((window) => (
              <div key={window.window} className="tj-card">
                <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">
                  {window.window} usage
                </p>
                <p className="serif-heading text-xl">
                  ${window.cost_usd.toFixed(4)}
                </p>
                <p className="text-xs text-[var(--tj-muted)]">
                  {window.tokens.toLocaleString()} tokens ·{" "}
                  {window.by_tier
                    .map((tier) => `${tier.tier}: ${tier.tokens.toLocaleString()}`)
                    .join(" · ") || "no calls"}
                </p>
              </div>
            ))}
          </section>

          <section className="space-y-3" aria-label="Tasks">
            {data.rows.length === 0 ? (
              <div className="tj-card">
                <p className="text-sm text-[var(--tj-muted)]">
                  No tasks match this filter. Create one from a finalized template to start running it.
                </p>
              </div>
            ) : (
              data.rows.map((row) => (
                <div key={row.task_id} className="tj-card" data-testid="dashboard-row">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <Link
                        to="/tasks/$taskId"
                        params={{ taskId: row.task_id }}
                        className="serif-heading text-lg"
                      >
                        {row.name}
                      </Link>
                      <p className="text-xs text-[var(--tj-muted)]">
                        {row.latest_run
                          ? `${row.latest_run.status} · ${duration(row.latest_run.duration_ms)} · ${row.latest_run.trigger}`
                          : "never run"}
                        {row.next_fire_at ? ` · next ${row.next_fire_at}` : ""}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={statusClass(row.status)}>{row.status}</span>
                      <span className="tj-badge">{verificationBadge(row)}</span>
                      {row.latest_run ? (
                        <span className="tj-badge">
                          {row.latest_run.usage.tokens.toLocaleString()} tok · $
                          {row.latest_run.usage.cost_usd.toFixed(3)}
                        </span>
                      ) : null}
                      {row.latest_run?.error ? (
                        <span className="tj-badge tj-badge-red">{row.latest_run.error.code}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}
